import React, { useState, useEffect } from 'react'
import SelectField from '../common/SelectField.js';
import Alert from '../common/Alert'
import tripContractService from '../../services/tripContractService'
import { X, Send } from 'lucide-react'
import { validators, inputFilters, useValidation, ValidationMessage } from '../common/Validation.js'
import Button from '../common/Button.js'
import { useTranslation } from 'react-i18next';


const BatchPaymentModal = ({ isOpen, partyId, partyName, onClose }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [trips, setTrips] = useState([])
  const [formData, setFormData] = useState({
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_mode: 'cash',
    reference_number: '',
    remarks: ''
  })
  const { errors, validate, validateField } = useValidation()

  const validationRules = {
    amount:       [(v) => validators.required(v, 'Payment amount'), (v) => validators.number(v, 0.01)],
    payment_date: (v) => validators.required(v, 'Payment date'),
    payment_mode: (v) => validators.select(v, 'payment mode'),
  }

  const [allocation, setAllocation] = useState({})

  useEffect(() => {
    if (isOpen && partyId) {
      fetchPartyTrips()
    }
  }, [isOpen, partyId])

  const fetchPartyTrips = async () => {
    try {
      setLoading(true)
      const response = await tripContractService.getPartyTrips(partyId, {
        status: 'pending'
      })
      setTrips(response.data.data || [])
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to load party trips')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    let filtered = value
    if (name === 'amount') filtered = inputFilters.amount(value)
    setFormData({ ...formData, [name]: filtered })

    if (validationRules[name]) {
      validateField(name, filtered, validationRules[name])
    }
  }

  const calculateTotalDue = () => {
    return trips.reduce((sum, trip) => {
      const balance = (trip.total_cost || 0) - (trip.paid_amount || 0)
      return sum + balance
    }, 0)
  }

  const calculateAllocation = () => {
    const amount = parseFloat(formData.amount || 0)
    const newAllocation = {}
    let remaining = amount

    // Allocate FIFO (oldest first - based on trip order)
    trips.forEach((trip, index) => {
      if (remaining <= 0) {
        newAllocation[trip.id] = 0
        return
      }

      const balance = (trip.total_cost || 0) - (trip.paid_amount || 0)
      const allocated = Math.min(remaining, balance)
      newAllocation[trip.id] = parseFloat(allocated.toFixed(2))
      remaining -= allocated
    })

    setAllocation(newAllocation)
  }

  useEffect(() => {
    if (formData.amount) {
      calculateAllocation()
    }
  }, [formData.amount, trips])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate(formData, validationRules)) return

    const totalDue = calculateTotalDue()
    if (parseFloat(formData.amount) > totalDue) {
      Alert.error('Payment amount cannot exceed total due of ' + formatCurrency(totalDue))
      return
    }

    try {
      setSubmitting(true)
      const payload = {
        amount: parseFloat(formData.amount),
        payment_date: formData.payment_date,
        payment_mode: formData.payment_mode,
        reference_number: formData.reference_number,
        remarks: formData.remarks,
        allocation: allocation
      }

      await tripContractService.batchPayment(partyId, payload)
      Alert.success('Batch payment processed successfully!')
      onClose()
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to process batch payment')
      console.error(err)
    } finally {
      setSubmitting(false)
    }
  }

  const formatCurrency = (value) => {
    return parseFloat(value || 0).toLocaleString('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  if (!isOpen) return null

  const totalDue = calculateTotalDue()

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Batch Payment</h2>
            <p className="text-sm text-gray-600">Party: {partyName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        {loading ? (
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto mb-3"></div>
            <p className="text-gray-600">Loading outstanding trips...</p>
          </div>
        ) : trips.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            No outstanding trips found for this party
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Total Due Info */}
            <div className="bg-blue-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-600 mb-1">Total Outstanding Balance</p>
              <p className="text-2xl font-bold text-blue-600">{formatCurrency(totalDue)}</p>
              <p className="text-xs text-gray-600 mt-2">{trips.length} outstanding trip(s)</p>
            </div>

            {/* Payment Form */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Amount *
                </label>
                <input
                  type="text"
                  name="amount"
                  value={formData.amount}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-blue-400 ${
                    errors.amount ? 'border-red-500' : 'border-gray-200'
                  }`}
                />
                <ValidationMessage error={errors.amount} />
              </div>

              {/* Payment Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Date *
                </label>
                <input
                  type="date"
                  name="payment_date"
                  value={formData.payment_date}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-blue-400 ${
                    errors.payment_date ? 'border-red-500' : 'border-gray-200'
                  }`}
                />
                <ValidationMessage error={errors.payment_date} />
              </div>

              {/* Payment Mode */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Mode *
                </label>
                <SelectField variant="form"
                  name="payment_mode"
                  value={formData.payment_mode}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-blue-400 ${
                    errors.payment_mode ? 'border-red-500' : 'border-gray-200'
                  }`}
                >
                  <option value="cash">Cash</option>
                  <option value="cheque">Cheque</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="upi">UPI</option>
                  <option value="card">Card</option>
                  <option value="other">Other</option>
                </SelectField>
                <ValidationMessage error={errors.payment_mode} />
              </div>

              {/* Reference Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reference Number (Optional)
                </label>
                <input
                  type="text"
                  name="reference_number"
                  value={formData.reference_number}
                  onChange={handleInputChange}
                  placeholder="e.g., Cheque No"
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
                />
              </div>
            </div>

            {/* Remarks */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Remarks (Optional)
              </label>
              <textarea
                name="remarks"
                value={formData.remarks}
                onChange={handleInputChange}
                placeholder="Add any additional notes"
                rows="2"
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
              />
            </div>

            {/* Allocation Preview */}
            {formData.amount && Object.keys(allocation).length > 0 && (
              <div className="bg-green-50 rounded-lg p-4">
                <h4 className="font-semibold text-green-900 mb-3">Payment Allocation (FIFO)</h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {trips.map((trip) => {
                    const allocatedAmount = allocation[trip.id] || 0
                    const balance = (trip.total_cost || 0) - (trip.paid_amount || 0)
                    return allocatedAmount > 0 ? (
                      <div key={trip.id} className="flex justify-between items-center text-sm p-2 bg-white rounded">
                        <div>
                          <p className="font-medium text-gray-900">{trip.trip_id}</p>
                          <p className="text-xs text-gray-600">Balance: {formatCurrency(balance)}</p>
                        </div>
                        <span className="font-semibold text-green-600">
                          {formatCurrency(allocatedAmount)}
                        </span>
                      </div>
                    ) : null
                  })}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t border-gray-200">
              <Button variant="secondary" onClick={onClose} className="flex-1">
                Cancel
              </Button>
              <Button variant="success" type="submit" loading={submitting} icon={Send} className="flex-1">
                {submitting ? 'Processing...' : 'Process Payment'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

export default BatchPaymentModal
